import {Modal} from 'antd'
import {getId, getType} from 'apis/model/base'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {NullAsDefault} from 'views/Shared'
import {TimetableActionTypes} from '..'
import {showDeleteConfirmDialog} from '../../factory/createConfirmDialog'
import createContextMenu from '../../factory/createContextMenu'
import {notifyOnError} from '../../factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from '../../factory/createNotification'
import {
    CourseClassTimetableCopyModal,
    CourseClassTimetableCreationModal,
    CourseClassTimetableEditingModal,
} from './CourseClassTimetableForm'
import {BiSolidEdit, BiTrash} from "react-icons/bi";
import {
    course_class_createTimetable_Api,
    course_class_deleteTimetable_Api,
    course_class_updateTimetable_Api
} from "../../../../apis";
import {MdOutlineControlPointDuplicate} from "react-icons/md";
import {renderIf} from "../../../Shared";

const useCourseClassTimetable = ({item, ...props} = {}) => {
    const t = useTranslate()

    const {refreshKey, setRefreshKey = NullAsDefault} = props;

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const onError = notifyOnError(t)

    const onCancel = () => {
        setAction({})
    }

    const onRefresh = () => {
        setRefreshKey(Date.now())
        onCancel()
    }

    const {handleAsyncAction: onAssign} = useAsyncAction({
        apiInfo: course_class_createTimetable_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.INFORMATION, t)
            onRefresh()
        },
    })

    const {handleAsyncAction: onUpdate} = useAsyncAction({
        apiInfo: course_class_updateTimetable_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onCancel()
        },
    })

    const {handleAsyncAction: onRevoke} = useAsyncAction({
        apiInfo: course_class_deleteTimetable_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.INFORMATION, t)
            onRefresh()
        },
    })

    const handleAction = (action, params) => {
        switch (action) {
            case TimetableActionTypes.TIMETABLE_CREATE:
            case TimetableActionTypes.TIMETABLE_UPDATE:
            case TimetableActionTypes.TIMETABLE_DUPLICATE:
                setAction({type: action, value: params})
                break
            case TimetableActionTypes.TIMETABLE_DELETE:
                const id = params?.id
                if (id) {
                    showDeleteConfirmDialog({
                        translate: t,
                        title: 'delete timetable item',
                        message: 'are you sure you want to delete this timetable item',
                        onOk: () => {
                            onRevoke({}, {id: id})
                        },
                    })
                }
                break
            default:
                break
        }
    }

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <MdOutlineControlPointDuplicate size={14} className="text-green-500"/>,
                        label: 'duplicate',
                        key: TimetableActionTypes.TIMETABLE_DUPLICATE,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: TimetableActionTypes.TIMETABLE_UPDATE,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: TimetableActionTypes.TIMETABLE_DELETE,
                    },
                ].filter((e) => item),
                (key) => {
                    handleAction(key, item)
                }
            ),
        []
    )

    const modal = useMemo(() => {
        let title
        let Content = NullAsDefault
        let params = {title: undefined}
        let modal_params = {}

        const {Header = NullAsDefault} = props ?? {}

        const course_class = item ?? action?.value?.course_class ?? {}

        const {owner, course} = course_class ?? {}

        const course_id = getId(course)

        const course_class_id = getId(course_class)

        const [owner_id, owner_type] = [getId(owner), getType(owner)]

        switch (action?.type) {
            case TimetableActionTypes.TIMETABLE_CREATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('add timetable for the class')}
                    </div>
                )
                params = {
                    onCancel,
                    course_id,
                    course_class_id,
                    onSubmit: (values) => {
                        onAssign(values, {
                            course_class_id,
                        })
                    },
                }
                modal_params = {
                    width: 640,
                }
                Content = CourseClassTimetableCreationModal
                break
            case TimetableActionTypes.TIMETABLE_UPDATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('edit timetable for the class')}
                    </div>
                )
                params = {
                    onCancel,
                    course_id,
                    course_class_id,
                    owner_type,
                    owner_id,
                    item: action?.value,
                    onSubmit: (values) => {
                        onUpdate(values, {
                            id: action?.value?.id,
                        })
                    },
                }
                modal_params = {
                    width: 700,
                }
                Content = CourseClassTimetableEditingModal
                break
            case TimetableActionTypes.TIMETABLE_DUPLICATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('duplicate timetable')}
                    </div>
                )
                params = {
                    onCancel,
                    course_id,
                    course_class_id,
                    owner_type,
                    owner_id,
                    item: action?.value,
                    onSubmit: (values) => {
                        onAssign(values, {
                            course_class_id,
                        })
                    },
                }
                modal_params = {
                    width: 700,
                }
                Content = CourseClassTimetableCopyModal
                break
            default:
                break
        }
        return (
            <Modal
                footer={null}
                title={title}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <div className="flex flex-col gap-3">
                    {renderIf(
                        action?.type === TimetableActionTypes.TIMETABLE_DUPLICATE,
                        <div className="space-x-1 text-sm italic">
                            <span className="font-bold text-color-100">
                                {`* ${t('note')} : `}
                            </span>
                            <span className="text-color-400">
                                {t('duplicate timetable description')}
                            </span>
                        </div>
                    )}
                    <Header item={course_class}/>
                    <Content
                        {...params}
                        {...{owner_id, owner_type}}
                    />
                </div>
            </Modal>
        )
    }, [item, action, props.Header])

    return {
        modal,
        action,
        onRefresh,
        onCancel,
        refreshKey,
        handleAction,
        renderContextMenu,
    }
}

export default useCourseClassTimetable
